import classNames from 'classnames';
import { useCallback, useContext, useMemo } from 'react';
import { useDeleteClientMutation } from '@/modules/common/queries/clients.ts';
import { EditClientModal } from '@/modules/admin-dashboard/components/modals/EditClientModal.tsx';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  SubduedTextSet,
} from 'ui';
import { DotsHorizontalIcon } from 'ui/icons';
import { ClientResponse } from 'compliance-api';

interface ClientItemProps {
  client: ClientResponse;
}

const ClientItem = ({ client }: ClientItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: deleteClient } = useDeleteClientMutation();

  const handleArchive = useCallback(async () => {
    await deleteClient(client.id);
  }, [client.id, deleteClient]);

  // Placeholder actions for dropdown menu
  const options = useMemo<DropdownMenuItem[]>(
    () => [
      {
        label: 'Edit',
        onClick: () => openModal(EditClientModal, { client }),
      },
      {
        label: 'Archive',
        onClick: () =>
          openModal(ConfirmModal, {
            onConfirm: handleArchive,
            title: (
              <p>
                Are you sure you want to archive{' '}
                <span className="text-blue-700">{client.name}</span>?
              </p>
            ),
          }),
      },
    ],
    [client, handleArchive, openModal],
  );

  return (
    <div
      className={classNames(
        'sm:border sm:rounded-xl border-gray-200 [&:not(:last-child)]:border-b',
        'bg-white',
      )}
    >
      <div
        className={classNames(
          'pl-2 sm:px-6 py-4 relative w-full grid grid-cols-[1fr_auto_auto] items-center gap-4 overflow-hidden',
        )}
      >
        {/* Name and numbers */}
        <div className="space-y-1.5 overflow-hidden">
          <a
            href={adminDashboardPaths.dynamic.client(client.id)}
            className="text-sm font-medium text-blue-700 hover:underline cursor-pointer truncate"
            onClick={e => {
              e.preventDefault();
              window.history.pushState({}, '', adminDashboardPaths.dynamic.client(client.id));
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                window.history.pushState({}, '', adminDashboardPaths.dynamic.client(client.id));
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            }}
            title={client.name}
          >
            {client.name}
          </a>
          <SubduedTextSet
            set={[
              `${client.number_of_tasks} Tasks`,
              `${client.number_of_events} Events`,
              `${client.number_of_reports} Reports`,
              `${client.number_of_documents} Docs`,
            ]}
          />
        </div>
        {/* Three dots menu */}
        <DropdownMenu
          autoPlace
          items={options}
          button={
            <button className="flex items-center justify-center">
              <img src={DotsHorizontalIcon} alt="Menu" />
            </button>
          }
          className="w-[160px] divide-white"
        />
      </div>
    </div>
  );
};

export default ClientItem;
